import PropTypes from 'prop-types';
import React from 'react';
import { compose } from 'redux';
import { Field, FieldHeader, ValidationMessage } from 'web-ui-components/lib/molecules/forms';
import { CHAPS_IFT_BACS_AmountLimit, exoticAmountLimit } from '../../../../config/index';
import withContent from '../../../../containers/withContent';
import withValidation from '../../../../containers/withValidation';
import {
	fixAmount,
	isAmountWithinLimit,
	sanitizeDecimalOccurrences,
	stripCurrencysIllegalCharacters,
} from '../../../../helpers/fieldHelpers';
import { getCurrencyDecimalPlaces } from '../../../../utils/countryUtils';
import CurrencyAmountField from '../CurrencyAmountField/CurrencyAmountField';
import howToSendContent, { contentPropTypes } from './HowToSendSection.content';

export const getAmountLimit = (isExoticCurrency) => {
	if (isExoticCurrency) {
		return exoticAmountLimit;
	}
	return CHAPS_IFT_BACS_AmountLimit;
};
export const HowToSendSection = ({
	content: { header, description, invalidValidationMsg },
	onChange,
	fromCurrency,
	onCurrencyRadioSelect,
	debitAmount,
	creditAmount,
	isValid,
	showValidation,
	sendCurrency,
	isSendAmountFromDebitCurrency,
	isExoticCurrency,
}) => {
	const showValidationMsg = !isValid && showValidation;
	const isDifferentCurrency = sendCurrency !== fromCurrency;
	const currencyDecimals = getCurrencyDecimalPlaces(sendCurrency);
	const handleOnBlur = (event) => onChange(fixAmount(event.target.value, currencyDecimals));
	const handleOnChange = (event) => {
		const amount = stripCurrencysIllegalCharacters(event.target.value, currencyDecimals);
		const sanitizedAmount = sanitizeDecimalOccurrences(amount);

		if (isAmountWithinLimit(sanitizedAmount, getAmountLimit(isExoticCurrency))) {
			onChange(sanitizedAmount);
		}
	};
	return (
		<Field>
			<FieldHeader name="how-to-send" titleText={header} description={description} />
			<CurrencyAmountField
				id={sendCurrency}
				currencyCode={sendCurrency}
				amount={creditAmount}
				handleOnChange={handleOnChange}
				onRadioChange={() => {
					if (isSendAmountFromDebitCurrency) {
						onCurrencyRadioSelect(false);
					}
				}}
				handleOnBlur={handleOnBlur}
				isRadioChecked={!isSendAmountFromDebitCurrency}
				showRadio={isDifferentCurrency}
				isDisabled={isSendAmountFromDebitCurrency}
			/>
			{isDifferentCurrency && (
				<CurrencyAmountField
					isDifferentCurrency={isDifferentCurrency}
					id={fromCurrency}
					currencyCode={fromCurrency}
					amount={debitAmount}
					handleOnChange={handleOnChange}
					onRadioChange={() => {
						if (!isSendAmountFromDebitCurrency) {
							onCurrencyRadioSelect(true);
						}
					}}
					isRadioChecked={isSendAmountFromDebitCurrency}
					showRadio
					isDisabled={!isSendAmountFromDebitCurrency}
				/>
			)}
			{showValidationMsg && <ValidationMessage hasIcon={false} value={invalidValidationMsg} />}
		</Field>
	);
};
HowToSendSection.propTypes = {
	content: PropTypes.shape(contentPropTypes),
	onChange: PropTypes.func.isRequired,
	onCurrencyRadioSelect: PropTypes.func.isRequired,
	fromCurrency: PropTypes.string.isRequired,
	debitAmount: PropTypes.string,
	creditAmount: PropTypes.string,
	isValid: PropTypes.bool.isRequired,
	showValidation: PropTypes.bool.isRequired,
	sendCurrency: PropTypes.string.isRequired,
	isSendAmountFromDebitCurrency: PropTypes.bool.isRequired,
	isExoticCurrency: PropTypes.bool.isRequired,
};

const validation = withValidation({
	validations: ['required', 'amount'],
	key: 'sendAmount',
	message: '',
});
export default compose(validation, withContent({ content: howToSendContent }))(HowToSendSection);
